import type { Json } from "@/shared/integrations/supabase/types";
import type { AuditExplanation } from "@/features/audit/types";

export type RawDeficiency = {
  id?: string | null;
  rule_id?: string | null;
  severity?: string | null;
  group?: string | null;
  title?: string | null;
  detail?: string | null;
  recommendation_summary?: string | null;
  asset?: {
    asset_id?: string | null;
    name?: string | null;
    category?: string | null;
    age_years?: number | null;
    manufacturer?: string | null;
    model_number?: string | null;
  } | null;
  survey_field?: string | null;
  survey_field_id?: string | null;
  explanation?: AuditExplanation | null;
};

export type RawRecommendation = {
  id?: string | null;
  deficiency_id?: string | null;
  rule_id?: string | null;
  deficiency_rule_id?: string | null;
  deficiency_asset_id?: string | null;
  section?: string | null;
  line_item?: string | null;
  cost_estimate?: number | string | null;
  basis?: string | null;
  priority?: string | null;
};

export type DeficiencySeverity = "critical" | "high" | "medium" | "low";

export const DEFICIENCY_SEVERITY_META: Record<DeficiencySeverity, { label: string; className: string }> = {
  critical: { label: "Critical", className: "bg-red-500/15 text-red-300 ring-red-500/25" },
  high: { label: "High", className: "bg-orange-500/15 text-orange-300 ring-orange-500/25" },
  medium: { label: "Medium", className: "bg-amber-500/15 text-amber-300 ring-amber-500/25" },
  low: { label: "Low", className: "bg-slate-500/15 text-slate-300 ring-slate-500/25" },
};

export function normalizeDeficiencySeverity(severity?: string | null): DeficiencySeverity {
  const normalized = severity?.toLowerCase() ?? "";
  if (normalized === "critical") return "critical";
  if (normalized === "major" || normalized === "high") return "high";
  if (normalized === "medium" || normalized === "med") return "medium";
  return "low";
}

export type DeficiencyPayload = {
  deficiencies?: RawDeficiency[];
  recommendations?: RawRecommendation[];
};

export function parseJson<T>(value: Json | null | undefined, fallback: T): T {
  if (!value) return fallback;
  if (typeof value === "string") {
    try {
      return JSON.parse(value) as T;
    } catch {
      return fallback;
    }
  }
  return value as T;
}

export function getGroupLabel(group: string | null | undefined): string {
  if (!group) return "Other";
  return group
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

export function getDeficiencyTitle(deficiency: RawDeficiency): string {
  return deficiency.title || deficiency.asset?.name || deficiency.survey_field || "Deficiency";
}

export function getDeficiencyCategory(deficiency: RawDeficiency): string {
  return getGroupLabel(deficiency.group || deficiency.asset?.category);
}

export function getRecommendationCostEstimate(recommendation: RawRecommendation): number | null {
  const rawCost = recommendation.cost_estimate;
  if (rawCost == null || rawCost === "") return null;
  const cost = typeof rawCost === "number" ? rawCost : Number(String(rawCost).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(cost) ? cost : null;
}

export function severityToPriorityFromDeficiency(deficiency: RawDeficiency): DeficiencySeverity {
  return normalizeDeficiencySeverity(deficiency.severity);
}
